<template>
    <ais-instant-search
        :search-client="searchClient"
        index-name="pages"
        :routing="routing"
    >
        <ais-configure v-bind="searchParameters" :filters="staticFilter" :hits-per-page.camel="12" />
        <!-- <detect-changes @change="scrollTop" /> -->

        <div class="container">
            <div class="search">
                <div class="search__show-me">
                    <h2 class="show-me">Show me:</h2>
                </div>
                <div class="search__inputs">
                    <ais-search-box
                        :show-reset="true">
                        <template
                            slot-scope="{
                                currentRefinement,
                                isSearchStalled,
                                refine
                            }"
                        >
                            <div class="ais-SearchBox-form" novalidate>
                                <input
                                    type="search"
                                    class="ais-SearchBox-input"
                                    :value="currentRefinement"
                                    @input="refine($event.currentTarget.value)"
                                    :placeholder="`All ${searchCategory}`"
                                />
                                <BaseIcon v-if="currentRefinement != ''" class="clear-field" icon="cross-thick" @click.native="refine('')" />
                            </div>
                        </template>
                    </ais-search-box>
                    <AlgoliaSearchLocations @update="updateSearchParams"/>
                </div>
            </div>

            <div class="filters">
                <algolia-feature-filters attribute="productFeatures"></algolia-feature-filters>
            </div>

            <div class="categories">
                <ais-menu attribute="altCategories" :transform-items="categoryChildren" :limit="50" :sort-by="['name:asc']" class="ais-Menu">
                    <div slot-scope="{
                        items,
                        refine,
                    }">
                        <algolia-product-categories
                            :items="items"
                            :refine="refine">
                        </algolia-product-categories>
                    </div>
                </ais-menu>
            </div>
        </div>

        <div class="hits mb-xlarge">
            <div class="container">
                <div class="meta-info">
                    <ais-current-refinements>
                        <template slot-scope="{ items }">
                            <div v-if="items.length">
                                <ais-stats>
                                    <strong
                                        slot-scope="{ nbHits }"
                                        class="smaller text--grey-dark"
                                    >
                                        {{ nbHits }} results found
                                    </strong>
                                </ais-stats>
                            </div>
                        </template>
                    </ais-current-refinements>
                </div>
                <AlgoliaSearchClear :excluded-attributes="[]" @resetLocations="resetLocations">
                    <strong class="smaller text--blue mr-min">
                        Clear Filters
                    </strong>
                    <BaseIcon icon="reset" class="text--redpink" />
                </AlgoliaSearchClear>
            </div>
        </div>

        <div class="container">
            <ais-current-refinements :excluded-attributes="[]">
                <template slot-scope="{ items }">
                    <ais-infinite-hits class="mb-large" v-if="items.length > 0">
                        <template slot-scope="{ items, refineNext, isLastPage }">
                            <product-search-results
                                :items="items"
                                :searchCategory="searchCategory"
                                :fallback-image="fallbackImage">
                            </product-search-results>
                            <HrLine v-if="!isLastPage" />
                            <AlgoliaSearchLoadMore
                                class="AlgoliaSearchLoadMore"
                                v-if="!isLastPage"
                                @refine="refineNext"
                            />
                        </template>
                    </ais-infinite-hits>
                    <slot v-else />
                </template>
            </ais-current-refinements>
        </div>

        <!-- <div class="container lap-and-up-mt-large desk-mt-xlarge mb-medium mb-xlarge">
            <ais-hits>
                <template slot-scope="{ items }">
                    <div v-if="showResults">
                        <product-search-results :items="items"></product-search-results>
                    </div>
                    <slot v-else />
                </template>

            </ais-hits>
        </div> -->
    </ais-instant-search>
</template>
<script>
import algoliasearch from "algoliasearch/lite";
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import { simple as simpleMapping } from 'instantsearch.js/es/lib/stateMappings';
import moment from 'moment';

// Import the EventBus.
import { EventBus } from '@utils/event-bus.js';

function getFacetSlug(name) {
    return name
        .toLowerCase()
        .replace('&', 'and')
        .replace(', ', '--')
        .split(' ')
        .map(encodeURIComponent)
        .join('-');
}

function getFacetName(name) {
    if (!name) return;
    return name
        .replace(/--/g, ', ')
        .replace(/-and-/g, ' & ')
        .replace(/-/g, ' ')
        .split(' ')
        .map((e) => {
            `${e[0].toUpperCase()}${e.slice(1)}`
        })
        .join(' ');
}

const routing = {
    router: historyRouter({
        // windowTitle({ category, query }) {
        //     return "Visit Gippsland | What's On"
        // },

        createURL({ qsModule, routeState, location }) {
            const urlParts = location.href.match(/^(.*?)\/search/);
            const baseUrl = `${urlParts ? urlParts[1] : ''}/`;
            let regionPath;

            const locationRoute = routeState.region || routeState.surroundings;

            if (locationRoute) {
                const region = locationRoute[0].split(' > ');
                regionPath = `${getFacetSlug(region[0])}/${getFacetSlug(region[1])}/`;
            } else {
                regionPath = '';
            }

            const queryParameters = {};

            if (routeState.surroundings) {
                queryParameters.surroundings = true;
            }

            if (routeState.query) {
                queryParameters.query = encodeURIComponent(routeState.query);
            }

            if (routeState.category) {
                queryParameters.category = getFacetSlug(routeState.category);
            }

            const queryString = qsModule.stringify(queryParameters, {
                addQueryPrefix: true,
                arrayFormat: 'repeat',
            });

            return `${baseUrl}/${regionPath}${queryString}`;
        },

        parseURL({ qsModule, location }) {
            const pathnameMatches = location.pathname.match(/events\/([^/]*)\/?([^/]*)/);

            let suburb;
            let city;
            let region;
            let surroundings;

            if (pathnameMatches && pathnameMatches[1]) {
                suburb = getFacetName(pathnameMatches && pathnameMatches[1]);
            } else {
                suburb = '';
            }

            if (pathnameMatches && pathnameMatches[1]) {
                city = getFacetName(pathnameMatches && pathnameMatches[2]);
            } else {
                city = '';
            }

            const queryStrings = qsModule.parse(
                location.search.slice(1)
            );

            if (suburb != '' && city != '') {
                if (queryStrings.surroundings) {
                    surroundings = [`${suburb} > ${city}`];
                    region;
                } else {
                    region = [`${suburb} > ${city}`];
                    surroundings;
                }
            } else {
                region;
                surroundings;
            }

            return {
                query: queryStrings.query,
                category: queryStrings.category,
                surroundings,
                region,
            };
        },
    }),

    stateMapping: {
        stateToRoute(uiState) {
            return {
                query: uiState.query,
                category: uiState.menu && uiState.menu['categories.lvl0'],
                region: uiState.refinementList && uiState.refinementList['regions.lvl1'],
                surroundings: uiState.refinementList && uiState.refinementList['surroundings.lvl1']
            };
        },

        routeToState(routeState) {
            return {
                query: routeState.query,
                menu: {
                    'categories.lvl0': routeState.category && getFacetName(routeState.category)
                },
                refinementList: {
                    'surroundings.lvl1': routeState.surroundings,
                    'regions.lvl1': routeState.region
                },
            };
        },
    },
};

export default {
    props: {
        fallbackImage: {
            type: String
        },
        searchCategory: {
            type: String,
            required: false
        },
        pageSearchCategory: {
            type: String,
            required: false
        }
    },

    data() {
        return {
            searchClient: algoliasearch(
                window.algoliaId,
                window.algoliaSearchKey
            ),
            routing: {
                router: routing.router,
                stateMapping: routing.stateMapping,
            },
            searchParameters: {},
            staticFilter: `categories.lvl0:"${this.searchCategory}" AND NOT title:"${this.pageSearchCategory}"`,
            showResults: true,
        };
    },

    methods: {
        scrollTop() {
            const el = document.querySelector('.BreadCrumbs');
            if (el) el.scrollIntoView(true);
        },

        clearSearch() {
            const searchInput = document.querySelector('.ais-SearchBox-input');
            if (searchInput) {
                searchInput.value = '';
            }
        },

        updateSearchParams(value) {
            this.searchParameters = value;
        },

        resetLocations() {
            EventBus.$emit('resetLocations', { towns: [], surrounding: false });
        },

        categoryChildren(items) {
            const children = items.reduce((acc, item) => {
                if (!item.label.startsWith(this.searchCategory)) return acc;

                item.label = item.label.split(' $ ')[1];
                acc.push(item);
                return acc;

            }, []);
            // console.log(result);
            return children;
        }
    }
};
</script>
<style lang="scss" scoped>
.search {
    display: flex;
    flex-direction: column;
    border-top: 1px solid getColour("grey", "medium");
    padding: getSpacing("medium") 0;

    @include media-query(desk) {
        flex-direction: row;
    }

    @include media-query(lap) {
        padding-bottom: getSpacing("large");
    }

    input[type="search"]::-webkit-search-decoration,
    input[type="search"]::-webkit-search-cancel-button,
    input[type="search"]::-webkit-search-results-button,
    input[type="search"]::-webkit-search-results-decoration {
      -webkit-appearance:none;
    }
}

.search__inputs {
    display: flex;
    flex: 1;

    @include media-query(palm) {
        display: block;
    }

    .ais-SearchBox,
    .locations {
        margin-top: 2px;
        flex: 1;
    }

    .locations {
        @include media-query(palm) {
            margin-top: 0;

            h2 {
                font-size: 2rem;
            }
        }
    }

    @include media-query(smart) {
        .ais-SearchBox {
            flex-basis: 40%;
        }
        .locations {
            flex-basis: 60%;
        }
    }
}

.show-me {
    font-family: $fancy-font-family;
    font-weight: 300;
    font-size: 2.25rem;
    line-height: 1;
    color: #000;
    white-space: nowrap;
    margin-right: 0.5em;
    flex: 0;

    @include media-query(lap-and-up) {
        width: auto;
    }

    @include media-query(palm) {
        margin-right: 0;
        margin-bottom: 1rem;
    }
}

::v-deep.clear-field {
    fill: rgba(60, 60, 60, 0.5);
    width: 10px;
    height: 10px;
    position: absolute;
    right: 12px;
    top: 50%;
}

::v-deep.ais-SearchBox {
    @include media-query(palm) {
        margin-bottom: 1rem;
    }

    @include media-query(lap) {
        width: 100%;
    }
}

::v-deep .ais-SearchBox-form {
    position: relative;
    padding: 9px;
    padding-right: 23px;
    border-bottom: 2px solid getColour("blue");
}

::v-deep .ais-SearchBox-input {
    color: getColour('grey', 'dark');
    font-family: $base-font-family;
    outline: none;
    border: none;
    font-size: 19px;
    -webkit-appearance: none;
    padding: 0;
    margin: 0;
    width: 100%;

    @include media-query(desk-wide) {
        font-size: 22px;
    }

    @include media-query(tiny) {
        font-size: 16px;
    }
}

::-webkit-input-placeholder {
  color: getColour('grey', 'dark');
}


::v-deep .ais-SearchBox-submit {
    display: none;
}

.BaseButton {
    display: block!important;
    margin: auto;
    cursor: pointer;
    margin-bottom: getSpacing(large);
}

.HrLine {
    margin-bottom: getSpacing(large);
    padding: 0;
}

.hits {
    padding: 20px 0;
    background-color: getColour("grey", "light");
    margin-bottom: getSpacing("medium");

    .container {
        display: flex;
        justify-content: space-between;
    }
}

.categories {
    display: flex;
    align-items: center;
    padding: 30px 0;
    overflow: auto;
}

.feature-filters {
    border-bottom: 1px solid getColor("grey", "medium");
}
</style>
